<template>
  <div class="program">
    <toolbar transparent fixed :logo="false">
      <template v-slot:right>
        <toolbar-share-button />
      </template>
    </toolbar>

    <template v-if="isPresent(banner)">
      <hero :image="banner" />
    </template>
    <template v-else>
      <div class="v-toolbar__spacer"></div>
    </template>

    <text-block-header :heading="title" :content="description" />

    <program-stages
      :programId="id"
      :programTitle="title"
      :stageTitle="stage_title"
      :stages="stages"
      @closeCompleteDialog="showProgramCompletedForm"
    />

    <template v-if="ctaInfo">
      <cta-row white-bg row>
        <cta-row-price
          :priceText="ctaInfo.priceText"
          :subText="ctaInfo.priceSubText"
          :priceInterval="ctaInfo.priceInterval"
        />
        <v-button class="mr-0" :is-loading="isLoading" @click="handleCtaClick">
          {{ ctaInfo.buttonText }}
        </v-button>
      </cta-row>

      <template v-if="!is_free && isPresent(product)">
        <checkout-modal :product="product" @success="handleProgramPurchase" />
      </template>
    </template>

    <alert-dialog
      :actions="[
        {
          title: $t('components.pageTemplates.programTemplate.dialogViewReceipt'),
          callback: this.goToAccount
        },
        {
          title: $t('components.pageTemplates.programTemplate.dialogGoProgram'),
          callback: this.goToProgram
        }
      ]"
      :show="showSuccessDialog"
      @close="() => (this.showSuccessDialog = false)"
      centered
    >
      <v-icon icon="verified_user" size="4rem" color="var(--brand-color-hex)" />
      <p class="font-semi-bold">
        {{ $t("components.pageTemplates.programTemplate.dialogPurchaseSuccess") }}
      </p>
    </alert-dialog>

    <v-made-with-movement v-if="!isDesktop" />
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from "vuex"

import { isPresent } from "@/lib/utils"
import EventBus, { EVENTS } from "@/lib/event-bus"
import { productInterval } from "@/lib/product-interval"

import Toolbar from "@/components/Toolbar"
import ToolbarShareButton from "@/components/toolbar/ShareButton"
import Hero from "@/components/Hero"
import TextBlockHeader from "@/components/TextBlockHeader"
import ProgramStages from "@/components/program/Stages"

export default {
  components: {
    Toolbar,
    ToolbarShareButton,
    Hero,
    TextBlockHeader,
    ProgramStages,
    CheckoutModal: () => import("@/components/checkout/CheckoutModal"),
    CtaRow: () => import("@/components/CtaRow"),
    CtaRowPrice: () => import("@/components/cta-row/Price"),
    AlertDialog: () => import("@/components/AlertDialog")
  },

  props: {
    id: { type: Number, required: true },
    title: { type: String, default: "" },
    description: { type: String, default: "" },
    stage_title: { type: String, default: function(){ return this.$t('components.pageTemplates.programTemplate.content')} },
    is_free: { type: Boolean, default: true },
    is_private: { type: Boolean, default: false },
    banner: { type: Object, default: () => ({}) },
    product: { type: Object, default: null },
    stages: { type: Array, default: () => [] }
  },

  head() {
    return {
      title: this.title
    }
  },

  data() {
    return {
      showSuccessDialog: false,
      isLoading: false,
      hasAccessViaPack: false
    }
  },

  mounted() {
    if (this.isAuthenticated) {
      this.isLoading = true
      this.fetchProgramAccess(this.id)
        .then(res => {
          this.hasAccessViaPack = res.data.has_access_via_pack
        })
        .finally(() => (this.isLoading = false))
    }
  },

  computed: {
    ...mapState("responsive", ["isDesktop"]),
    ...mapGetters("auth", ["isAuthenticated", "currentUser"]),
    ...mapGetters("programs", ["isUserSubscribedToProgram"]),
    ...mapGetters("access", ["hasAccessViaSubscription"]),

    ctaInfo() {
      // TODO: add tests
      if (
        !this.hasActiveSubscription &&
        (this.is_free || this.hasAccessViaPack || this.hasAccessViaSubscription)
      ) {
        const DEFAULT = {
          buttonText: function() {
            return this.$t("components.pageTemplates.programTemplate.startProgram")
          },
          priceText: function() {
            return this.$t("components.pageTemplates.programTemplate.free")
          },
          priceSubText: function() {
            return this.$t("components.pageTemplates.programTemplate.startToday")
          }
        }

        if (this.is_free) {
          return DEFAULT
        } else if (this.hasAccessViaPack) {
          return {
            ...DEFAULT,
            priceSubText: function() {
              return this.$t("components.pageTemplates.programTemplate.viaPack")
            }
          }
        } else {
          DEFAULT
        }
      }

      if (!isPresent(this.product)) {
        return null
      }

      const priceText = `$${this.product.price_dollars}`

      if (!this.hasActiveSubscription) {
        if (this.isRecurring) {
          const priceInterval = `/ ${productInterval(this.product.interval)}`

          return {
            buttonText: function() {
              return this.$t("components.pageTemplates.programTemplate.subscribe")
            },
            priceText,
            priceSubText: function() {
              return this.$t("components.pageTemplates.programTemplate.cancelAnytime")
            },
            priceInterval
          }
        } else {
          return {
            buttonText: function() {
              return this.$t("components.pageTemplates.programTemplate.purchase")
            },
            priceText,
            priceSubText: function() {
              return this.$t("components.pageTemplates.programTemplate.startToday")
            }
          }
        }
      }

      return null
    },

    canAccessFree() {
      return this.is_free || this.hasAccessViaSubscription
    },

    isRecurring() {
      return this.product && this.product.purchase_type == "recurring"
    },

    hasActiveSubscription() {
      return this.isUserSubscribedToProgram(this.id)
    }
  },

  methods: {
    isPresent,
    productInterval,

    ...mapActions("programs", [
      "subscribe",
      "fetchProgramAccess",
      "fetchSubscriptions"
    ]),

    handleCtaClick() {
      if (
        this.isAuthenticated &&
        (this.is_free || this.hasAccessViaPack || this.hasAccessViaSubscription)
      ) {
        this.subscribeToProgram()
      } else if (!this.is_free && isPresent(this.product)) {
        this.$router.push("#checkout")
      } else if (!this.isAuthenticated) {
        this.$router.push("#auth")
      }
    },

    subscribeToProgram() {
      this.isLoading = true

      this.subscribe(this.id)
        .then(() => this.showProgramStartedForm())
        .catch(err => {
          this.$notify(err.response.data.message)
        })
        .finally(() => (this.isLoading = false))
    },

    showProgramStartedForm() {
      EventBus.$emit(EVENTS.PROGRAM_STARTED, { id: this.id })
    },

    showProgramCompletedForm() {
      EventBus.$emit(EVENTS.PROGRAM_COMPLETED, { id: this.id })
    },

    handleProgramPurchase() {
      this.showSuccessDialog = true
      this.fetchSubscriptions()
    },

    goToAccount() {
      this.showSuccessDialog = false
      this.$router.push("/me/account")
    },

    goToProgram() {
      this.showSuccessDialog = false
      this.showProgramStartedForm()
    }
  }
}
</script>
